import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

interface Question { id?: number; text: string; question_type: 'open_text' | 'rating' | 'checklist' | 'rating_text'; is_required: boolean; order_index: number; options: string[] | null; rating_scale: number; }
interface Template { id: number; name: string; description?: string | null; respondent_layer: string; about_role: string; questions: Question[]; }

const layerLabel: Record<string, string> = { employee: 'עובד', manager: 'מנהל', hq: 'מטה', ceo: 'מנהלת הרשות', all: 'כולם' };
const aboutLabel: Record<string, string> = { self: 'הערכה עצמית', manager: 'על מנהל', employee: 'על עובד', hq: 'על מטה', ceo: 'על מנהלת הרשות', org_survey: 'סקר ארגוני' };
const qtypeLabel: Record<string, string> = { open_text: 'שאלה פתוחה', rating: 'דירוג', checklist: 'רשימת בחירה', rating_text: 'דירוג + הסבר' };

export default function AdminQuestionnaires() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [form, setForm] = useState({ name: '', description: '', respondent_layer: 'employee', about_role: 'self' });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [saving, setSaving] = useState(false);
  const [newOption, setNewOption] = useState<string>('');

  // questionnaires of OTHER cycles (e.g. last year's) so questions can be copied from them too
  const [otherTemplates, setOtherTemplates] = useState<(Template & { cycleName: string })[]>([]);

  const load = () => api.get(`/questionnaires/cycle/${cycleId}`).then(({ data }) => { setTemplates(data); setLoading(false); });
  useEffect(() => {
    load();
    api.get('/cycles/').then(async ({ data: cycles }) => {
      const out: (Template & { cycleName: string })[] = [];
      for (const c of cycles.filter((c: any) => String(c.id) !== String(cycleId))) {
        try { const { data } = await api.get(`/questionnaires/cycle/${c.id}`); data.forEach((t: Template) => out.push({ ...t, cycleName: c.name })); } catch { /* ignore */ }
      }
      setOtherTemplates(out);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  const openCreate = () => {
    setEditTemplate(null);
    setForm({ name: '', description: '', respondent_layer: 'employee', about_role: 'self' });
    setQuestions([]);
    setShowModal(true);
  };

  const openEdit = (t: Template) => {
    setEditTemplate(t);
    setForm({ name: t.name, description: t.description || '', respondent_layer: t.respondent_layer, about_role: t.about_role });
    setQuestions(t.questions.map(q => ({ ...q })));
    setShowModal(true);
  };

  const addQuestion = (type: 'open_text' | 'rating' | 'checklist' | 'rating_text') => {
    setQuestions(prev => [...prev, {
      text: '',
      question_type: type,
      is_required: true,
      order_index: prev.length,
      options: type === 'checklist' ? [] : null,
      rating_scale: 5,
    }]);
  };

  const updateQuestion = (idx: number, field: string, value: any) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (idx: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const addOption = (qIdx: number, option: string) => {
    if (!option.trim()) return;
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: [...(q.options || []), option.trim()] } : q));
  };

  const removeOption = (qIdx: number, optIdx: number) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: (q.options || []).filter((_, oi) => oi !== optIdx) } : q));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, questions: questions.map((q, i) => ({ ...q, order_index: i })) };
      if (editTemplate) {
        await api.put(`/questionnaires/${editTemplate.id}`, payload);
      } else {
        await api.post(`/questionnaires/cycle/${cycleId}`, payload);
      }
      setShowModal(false);
      await load();
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading"><div className="spinner" />טוען...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/cycles')}>← חזור</button>
        <h1 style={{ marginTop: '12px' }}>עריכת שאלונים</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={openCreate}>+ שאלון חדש</button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📝</div>
          <h3>אין שאלונים עדיין</h3>
          <p>הוסף שאלונים לפני הפעלת התהליך</p>
        </div>
      ) : (
        <div>
          {templates.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3>{t.name}</h3>
                  <div className="text-muted text-sm" style={{ marginTop: '4px' }}>
                    ממלא: {layerLabel[t.respondent_layer]} | על: {aboutLabel[t.about_role]} | {t.questions.length} שאלות
                  </div>
                  {t.description && <div className="text-muted text-sm" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{t.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {t.about_role === 'org_survey' && <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/survey/${t.id}`)}>תוצאות</button>}
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/questionnaires/${t.id}/preview`)}>תצוגה מקדימה</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)}>ערוך</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px' }}>
            <div className="modal-title">{editTemplate ? 'עריכת שאלון' : 'שאלון חדש'}</div>

            <div className="form-group">
              <label className="form-label">שם השאלון</label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="לדוגמה: הערכה עצמית לעובד" />
            </div>
            <div className="form-group">
              <label className="form-label">הנחיות למילוי (יוצגו לממלא מעל השאלות)</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="לדוגמה: דרג/י כל היגד מ-1 (כלל לא) עד 5 (במידה רבה מאוד). ניתן להוסיף הסבר חופשי." />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">מי ממלא</label>
                <select className="form-input" value={form.respondent_layer} onChange={e => setForm(p => ({ ...p, respondent_layer: e.target.value }))}>
                  <option value="employee">עובד</option>
                  <option value="manager">מנהל</option>
                  <option value="hq">מטה</option>
                  <option value="ceo">מנהלת הרשות</option>
                  <option value="all">כולם (כל העובדים, המנהלים והמטה)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">על מי</label>
                <select className="form-input" value={form.about_role} onChange={e => setForm(p => ({ ...p, about_role: e.target.value }))}>
                  <option value="self">עצמו</option>
                  <option value="manager">המנהל שלו</option>
                  <option value="employee">עובד ישיר</option>
                  <option value="hq">אנשי מטה</option>
                  <option value="ceo">מנהלת הרשות</option>
                  <option value="org_survey">סקר ארגוני</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '20px', marginBottom: '12px' }}>
              <h3>שאלות ({questions.length})</h3>
              {(templates.filter(t => !editTemplate || t.id !== editTemplate.id).length > 0 || otherTemplates.length > 0) && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                  <label className="form-label" style={{ margin: 0 }}>העתק שאלות משאלון אחר:</label>
                  <select className="form-input" style={{ width: 'auto' }} value="" onChange={e => {
                    const src = [...templates, ...otherTemplates].find(t => t.id === parseInt(e.target.value));
                    if (!src) return;
                    if (questions.length > 0 && !window.confirm('להחליף את השאלות הקיימות בשאלות מהשאלון שנבחר?')) return;
                    setQuestions(src.questions.map((q, i) => ({ text: q.text, question_type: q.question_type, is_required: q.is_required, order_index: i, options: q.options ? [...q.options] : null, rating_scale: q.rating_scale })));
                  }}>
                    <option value="">בחר/י שאלון...</option>
                    <optgroup label="בתהליך זה">
                      {templates.filter(t => !editTemplate || t.id !== editTemplate.id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </optgroup>
                    {Array.from(new Set(otherTemplates.map(t => t.cycleName))).map(cn => (
                      <optgroup key={cn} label={`מתהליך: ${cn}`}>
                        {otherTemplates.filter(t => t.cycleName === cn).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <span className="text-muted text-sm">כדי שההערכה העצמית ומשוב המנהל/ת יוצגו זה מול זה במסמך, השאלות צריכות להיות זהות.</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addQuestion('open_text')}>+ שאלה פתוחה</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addQuestion('rating')}>+ דירוג</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addQuestion('checklist')}>+ רשימת בחירה</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addQuestion('rating_text')}>+ דירוג + הסבר</button>
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', paddingLeft: '4px' }}>
              {questions.map((q, idx) => (
                <div key={idx} className="question-block" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <span className="badge badge-active" style={{ whiteSpace: 'nowrap' }}>{qtypeLabel[q.question_type]}</span>
                    <span className="text-muted text-sm">שאלה {idx + 1}</span>
                    <button type="button" style={{ marginRight: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-600)', fontSize: '16px' }} onClick={() => removeQuestion(idx)}>✕</button>
                  </div>

                  <textarea
                    className="form-input"
                    value={q.text}
                    onChange={e => updateQuestion(idx, 'text', e.target.value)}
                    placeholder="טקסט השאלה..."
                    rows={2}
                  />

                  {(q.question_type === 'rating' || q.question_type === 'rating_text') && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
                      <label className="form-label" style={{ margin: 0 }}>סקאלה:</label>
                      <select className="form-input" style={{ width: 'auto' }} value={q.rating_scale} onChange={e => updateQuestion(idx, 'rating_scale', parseInt(e.target.value))}>
                        <option value={5}>1-5</option>
                        <option value={10}>1-10</option>
                      </select>
                    </div>
                  )}

                  {q.question_type === 'checklist' && (
                    <div style={{ marginTop: '8px' }}>
                      {(q.options || []).map((opt, oi) => (
                        <div key={oi} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ flex: 1, fontSize: '14px' }}>• {opt}</span>
                          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-600)' }} onClick={() => removeOption(idx, oi)}>✕</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <input className="form-input" placeholder="אפשרות חדשה..." value={newOption} onChange={e => setNewOption(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { addOption(idx, newOption); setNewOption(''); } }} />
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { addOption(idx, newOption); setNewOption(''); }}>הוסף</button>
                      </div>
                    </div>
                  )}

                  <label className="checklist-item" style={{ marginTop: '8px' }}>
                    <input type="checkbox" checked={q.is_required} onChange={e => updateQuestion(idx, 'is_required', e.target.checked)} />
                    <span className="text-sm">שדה חובה</span>
                  </label>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>ביטול</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name}>
                {saving ? 'שומר...' : 'שמור שאלון'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
